/**
 * ExplanationScreen.tsx - Explanation of institutional nudging (NutriScore/EnergyLabel)
 * Last updated: 2025-08-16
 * Updated by: JulianPolly
 */

import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@blueprintjs/core";
import NutriScoreImg from "./assets/nutriscore.png";
import { useLanguage } from "../../../../context/LanguageContext";
import { de as deTranslations } from "../../../../translations/de";
import { en as enTranslations } from "../../../../translations/en";

function useGlossaryTerms(language: string) {
    if (language === "de") {
        return deTranslations.glossary?.terms || {};
    } else if (language === "en") {
        return enTranslations.glossary?.terms || {};
    }
    return deTranslations.glossary?.terms || {};
}

function resolveGlossarEntry(
    glossar: Record<string, any>,
    key: string
): { key: string; text: string } | undefined {
    const seen = new Set<string>();
    let current = key;
    while (current && !seen.has(current)) {
        seen.add(current);
        const entry = glossar[current];
        if (typeof entry === "string") return { key: current, text: entry };
        if (entry && typeof entry === "object" && "alias" in entry) {
            current = entry.alias;
        } else {
            break;
        }
    }
    return undefined;
}

const BegriffTooltip: React.FC<{ begriff: string; children: React.ReactNode; glossar: Record<string, any> }> = ({ begriff, children, glossar }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        if (!visible) return;
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setVisible(false);
            }
        };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [visible]);

    const resolved = resolveGlossarEntry(glossar, begriff);

    return (
        <span
            ref={ref}
            style={{
                position: "relative",
                cursor: "pointer",
                background: "#e7f1fa",
                color: "#2563eb",
                borderRadius: 5,
                padding: "0 4px",
                fontWeight: 500,
                zIndex: 10,
                display: "inline-block"
            }}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onClick={e => {
                setVisible((v: boolean) => !v);
                e.stopPropagation();
            }}
            tabIndex={0}
            aria-label={`Begriffserklärung für ${begriff}`}
        >
            <>
                {children}
                {visible && (
                    <span
                        style={{
                            position: "absolute",
                            left: "50%",
                            bottom: "100%",
                            transform: "translate(-50%, 12px)",
                            minWidth: 320,
                            maxWidth: 600,
                            background: "#fff",
                            color: "#222",
                            border: "1.5px solid #bcd3f6",
                            borderRadius: 14,
                            boxShadow: "0 10px 38px 0 rgba(0,0,0,0.17)",
                            zIndex: 1050,
                            padding: "22px 24px",
                            fontWeight: 400,
                            fontSize: 16,
                            lineHeight: 1.6,
                            pointerEvents: "auto",
                            whiteSpace: "pre-wrap",
                            marginTop: 12
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <b>{resolved?.key ?? begriff}</b>
                        <br />
                        {resolved?.text ?? begriff}
                    </span>
                )}
            </>
        </span>
    );
};

function parseWithGlossarTooltip(
    text: string,
    terms: string[],
    used: Set<string>,
    glossar: Record<string, any>
) {
    if (typeof text !== "string") return null;
    const glossaryKeyMap: Record<string, string> = {};
    terms.forEach(k => { glossaryKeyMap[k.toLowerCase()] = k; });

    const re = new RegExp(
        `\\b(${terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`,
        "gi"
    );
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let i = 0;

    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        const matched = match[1];
        const glossaryKey = glossaryKeyMap[matched.toLowerCase()] || matched;

        if (!used.has(glossaryKey)) {
            used.add(glossaryKey);
            parts.push(
                <BegriffTooltip begriff={glossaryKey} glossar={glossar} key={glossaryKey + "-" + i}>
                    {matched}
                </BegriffTooltip>
            );
        } else {
            parts.push(matched);
        }
        lastIndex = match.index + matched.length;
        i++;
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    return parts;
}

export function ExplainationScreen(props: any) {
    const { language } = useLanguage();
    const glossar = useGlossaryTerms(language);

    const SECTION_TITLES = [
        language === "en" ? "What is institutional nudging?" : "Was ist institutionelles Nudging?",
        language === "en" ? "How do labels like NutriScore and energy ratings affect us?" : "Wie wirken NutriScore und Energie-Labels?",
        language === "en" ? "Why do we react to such nudges?" : "Warum reagieren wir auf solche Nudges?"
    ];

    const paragraphs = [
        language === "en"
            ? "Institutional nudging means that organizations, companies, or the state shape our choices by re-designing the environment or providing information, without limiting our options. Labels like NutriScore or energy efficiency ratings are intended to help people make decisions that are better for their health or the environment."
            : "Institutionelles Nudging bedeutet, dass Organisationen, Unternehmen oder der Staat unsere Entscheidungen gezielt beeinflussen, indem sie die Umgebung oder Informationen verändern – ohne die Auswahl einzuschränken. Labels wie der NutriScore oder das Energieeffizienzlabel sollen helfen, gesündere oder nachhaltigere Entscheidungen zu treffen.",
        language === "en"
            ? "A NutriScore or an energy label can act as a powerful nudge, especially when the actual products are otherwise almost identical. Such labels stand out visually and quickly convey a message, sometimes even stronger than the nutritional or technical details. This can make an option seem healthier or more eco-friendly at a glance."
            : "Ein NutriScore oder ein Energie-Label kann als starker Nudge wirken, besonders wenn die Produkte ansonsten fast identisch sind. Die Labels stechen optisch hervor und vermitteln eine Botschaft – manchmal sogar stärker als die Nährwert- oder technischen Angaben. So kann eine Option auf den ersten Blick gesünder oder nachhaltiger wirken, auch wenn der Unterschied in Wirklichkeit gering ist.",
        language === "en"
            ? "We react to such nudges because labels simplify decisions, save time, and appeal to our desire to do the right thing for our health or the environment. Even though all the information is available, we tend to trust the signals that are easiest to process. This is why institutional nudges are used in many areas of everyday life."
            : "Wir reagieren auf solche Nudges, weil Labels Entscheidungen vereinfachen, Zeit sparen und unser Bedürfnis ansprechen, das Richtige für Gesundheit oder Umwelt zu tun. Obwohl alle Informationen da sind, verlassen wir uns oft auf die einfachsten Signale. Deshalb werden institutionelle Nudges in vielen Bereichen des Alltags eingesetzt."
    ];

    const ownExperimentNote = (
        <div
            style={{
                marginTop: 38,
                marginBottom: 38,
                background: "#f6f8fa",
                borderRadius: 11,
                padding: "26px 32px",
                fontSize: 17,
                lineHeight: 1.7,
                boxShadow: "0 2px 8px 0 rgba(0,0,0,0.06)",
                color: "#444",
                textAlign: "justify"
            }}
        >
            <div style={{
                display: "flex",
                alignItems: "flex-start",
                marginBottom: 16
            }}>
                <Icon
                    icon="lab-test"
                    size={20}
                    style={{
                        marginRight: 12,
                        marginTop: 2,
                        color: "#2563eb"
                    }}
                />
                <span style={{
                    fontWeight: 600,
                    fontSize: 18,
                    color: "#2563eb"
                }}>
                    {language === "en"
                        ? "How was this in your nudge experiment?"
                        : "Wie war das in eurem Nudge-Experiment?"}
                </span>
            </div>
            {language === "en"
                ? "In this experiment, you repeatedly made a choice between two very similar products. Sometimes, one of the options displayed a label (NutriScore or energy efficiency). You experienced how such labels can influence your decision, even when all other information is the same. In daily life, such nudges can affect not only food or electronics, but many other decisions as well!"
                : "In diesem Experiment hast du jeweils zwischen zwei sehr ähnlichen Produkten gewählt. Manchmal war auf einer der Optionen ein Label (NutriScore oder Energieeffizienz) abgebildet. Du hast selbst erlebt, wie solche Labels die Entscheidung beeinflussen können – selbst wenn alle anderen Informationen identisch sind. Im Alltag wirken solche Nudges nicht nur bei Lebensmitteln oder Technik, sondern in vielen Entscheidungen!"}
        </div>
    );

    const sourcesAPA = language === "en"
        ? [
            "Thaler, R. H., & Sunstein, C. R. (2008). Nudge: Improving Decisions About Health, Wealth, and Happiness. Yale University Press.",
            "van Kleef, E., & van Trijp, H. C. M. (2018). Healthy and Sustainable Food Choices: The Role of Nudges in Consumer Decision-Making. Current Opinion in Behavioral Sciences, 23, 141–146."
        ]
        : [
            "Thaler, R. H., & Sunstein, C. R. (2008). Nudge: Wie man kluge Entscheidungen anstößt. Econ.",
            "van Kleef, E., & van Trijp, H. C. M. (2018). Healthy and Sustainable Food Choices: The Role of Nudges in Consumer Decision-Making. Current Opinion in Behavioral Sciences, 23, 141–146."
        ];

    function parseParagraphsWithSections() {
        const terms = Object.keys(glossar).sort((a, b) => b.length - a.length);
        const used = new Set<string>();
        const parsed: React.ReactNode[] = [];

        for (let i = 0; i < SECTION_TITLES.length; ++i) {
            parsed.push(
                <div key={"section-title-" + i} style={{ marginTop: i === 0 ? 0 : 30, marginBottom: 8 }}>
                    <b style={{ fontSize: 20 }}>{SECTION_TITLES[i]}</b>
                </div>
            );
            if (i === 0) {
                parsed.push(
                    <img
                        src={NutriScoreImg}
                        alt="NutriScore Illustration"
                        style={{
                            float: "right",
                            marginLeft: 44,
                            marginBottom: 18,
                            width: 260,
                            maxWidth: "44%",
                            borderRadius: 16,
                            boxShadow: "0 6px 20px 0 rgba(0,0,0,0.09)",
                            objectFit: "cover"
                        }}
                    />
                );
            }
            parsed.push(
                <p key={`p-${i}`} style={{ marginTop: 0, marginBottom: 18, whiteSpace: "pre-line" }}>
                    {parseWithGlossarTooltip(paragraphs[i], terms, used, glossar)}
                </p>
            );
        }
        return parsed;
    }

    return (
        <div style={{
            maxWidth: 1250,
            margin: "0 auto",
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
            padding: "44px 64px"
        }}>
            <h2 style={{ marginBottom: 32 }}>
                {language === "en"
                    ? "How nudges and labels influence our decisions"
                    : "Wie Nudges und Labels unsere Entscheidungen lenken"}
            </h2>
            <div style={{
                fontSize: 17,
                lineHeight: 1.7,
                textAlign: "justify",
                position: "relative",
                minHeight: 260
            }}>
                {parseParagraphsWithSections()}
            </div>

            {ownExperimentNote}

            <div style={{
                background: "#f8f9fa",
                borderRadius: 8,
                padding: "22px 24px",
                marginTop: 36,
                fontSize: 15.5,
                color: "#444"
            }}>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8
                }}>
                    <Icon
                        icon="book"
                        size={16}
                        style={{
                            marginRight: 10,
                            color: "#2563eb"
                        }}
                    />
                    <b>{language === "en" ? "Sources:" : "Quellen:"}</b>
                </div>
                <ul style={{marginTop: 8, marginBottom: 0, paddingLeft: 24}}>
                    {sourcesAPA.map((src, index) => (
                        <li key={`source-${index}`}>{src}</li>
                    ))}
                </ul>
            </div>
        </div>
    );
}